import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { ReactSession } from "react-client-session";
import { tokens } from "../dashboard/theme";
import NavigationHeader from "../components/NavigationHeader";
import config from "./global/config";
import {
  AddClientModal,
  EditClientModal,
  DeleteClientModal,
} from "./ClientModals";
import { usePermissions } from "./hooks/usePermissions";

function Client() {
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [data, setData] = useState([]);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalDelete, setModalDelete] = useState(false);
  const [dataCategorie, setDataCategorie] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteClient, setDeleteClient] = useState(null); // Add this new state

  const [clientSelected, setClientSelected] = useState({
    id_client: "",
    Code_Client: "",
    categorie_code: "",
    nom: "",
    prenom: "",
    adresse: "",
    telephone: "",
    email: "",
    cin: "",
    grant: "",
    description: "",
    profession: "",
    user_upd: "",
  });
  const api = axios.create({
    baseURL: config.apiUrl,
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const clearSelected = () => {
    setClientSelected({
      id_client: "",
      Code_Client: "",
      categorie_code: "",
      nom: "",
      prenom: "",
      adresse: "",
      telephone: "",
      email: "",
      cin: "",
      grant: "",
      description: "",
      profession: "",
      user_cre: "",
      user_upd: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientSelected((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const openCloseModalInsert = () => {
    setModalAdd(false);
    clearSelected();
  };

  const openCloseModalEdit = () => {
    //console.log("Toggle edit modal, current state:", modalEdit);
    //console.log("Current client data:", clientSelected);
    setModalEdit(!modalEdit);
    if (modalEdit) {
      clearSelected();
    }
  };

  const openCloseModalDelete = () => {
    setModalDelete(!modalDelete);
    if (modalDelete) {
      setDeleteClient(null);
    }
  };

  const requestGet = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("client.php");
      setData(response.data);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      //console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestGetCategorie = async () => {
    try {
      const response = await api.get("selection.php", {
        params: { categorie: "" },
      });
      const categories = Array.isArray(response.data) ? response.data : [];
      setDataCategorie(categories);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      }
      //console.error("Error fetching categories:", error);
      setDataCategorie([]);
    }
  };

  const requestPost = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("METHOD", "POST");
      formData.append("categorie_code", clientSelected.categorie_code);
      formData.append("nom", clientSelected.nom);
      formData.append("prenom", clientSelected.prenom);
      formData.append("adresse", clientSelected.adresse || "");
      formData.append("telephone", clientSelected.telephone || "");
      formData.append("email", clientSelected.email || "");
      formData.append("cin", clientSelected.cin || "");
      formData.append("grant", clientSelected.grant || "");
      formData.append("description", clientSelected.description || "");
      formData.append("profession", clientSelected.profession || "");
      formData.append("user_cre", ReactSession.get("username"));

      // Log the formData to verify content
      for (let pair of formData.entries()) {
        //console.log(pair[0] + ": " + pair[1]);
      }

      const response = await api.post("client.php", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      openCloseModalInsert();
      await requestGet();
      clearSelected();
    } catch (error) {
      //console.error("Error details:", error);
      if (error.response) {
        //console.error("Response data:", error.response.data);
        //console.error("Response status:", error.response.status);
      }
      alert("Error adding client: " + (error.message || "Please try again."));
    } finally {
      setIsLoading(false);
    }
  };
  const requestPut = async () => {
    setIsLoading(true);
    try {
      //console.log("Updating client with data:", clientSelected);

      const formData = new FormData();
      // Add METHOD first
      formData.append("METHOD", "PUT");
      // Add all other fields
      formData.append("categorie_code", clientSelected.categorie_code);
      formData.append("nom", clientSelected.nom);
      formData.append("prenom", clientSelected.prenom);
      formData.append("adresse", clientSelected.adresse || "");
      formData.append("telephone", clientSelected.telephone || "");
      formData.append("email", clientSelected.email || "");
      formData.append("cin", clientSelected.cin || "");
      formData.append("grant", clientSelected.grant || "");
      formData.append("description", clientSelected.description || "");
      formData.append("profession", clientSelected.profession || "");
      formData.append("user_upd", ReactSession.get("username"));

      // Log the FormData contents
      for (let pair of formData.entries()) {
        //console.log(pair[0] + ": " + pair[1]);
      }

      const response = await api.post(
        `client.php?id_client=${clientSelected.id_client}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      openCloseModalEdit();
      await requestGet();
      clearSelected();
    } catch (error) {
      //console.error("Update error:", error);
      if (error.response) {
        //console.error("Response data:", error.response.data);
        //console.error("Response status:", error.response.status);
      }
      alert("Error updating client: " + (error.message || "Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const requestDelete = async () => {
    setIsLoading(true);
    try {
      if (!deleteClient?.id_client) {
        throw new Error("No client selected for deletion");
      }

      //console.log("Attempting to delete client:", deleteClient.id_client);

      const formData = new FormData();
      formData.append("METHOD", "DELETE");
      formData.append("user_del", ReactSession.get("username"));

      const response = await api.post(
        `client.php?id_client=${deleteClient.id_client}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      setModalDelete(false);
      await requestGet();
      setDeleteClient(null);
    } catch (error) {
      //console.error("Delete error:", error);
      alert("Error deleting client: " + (error.message || "Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const selectClient = (client, action) => {
    //console.log("Raw client data received:", client);

    // Create a complete client object
    const fullClientData = {
      id_client: client.id_client,
      Code_Client: client.Code_Client,
      categorie_code: client.categorie_code,
      nom: client.nom,
      prenom: client.prenom,
      adresse: client.adresse,
      telephone: client.telephone,
      email: client.email,
      cin: client.cin,
      grant: client.grant,
      description: client.description,
      profession: client.profession,
    };

    if (action === "Edit") {
      setClientSelected(fullClientData);
      openCloseModalEdit();
    } else if (action === "Delete") {
      setDeleteClient(fullClientData); // Use separate state for delete
      setModalDelete(true);
    }
  };

  const columns = [
    {
      field: "Code_Client",
      headerName: "Code Client",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "nom",
      headerName: "Nom",
      flex: 1,
    },
    {
      field: "prenom",
      headerName: "Prénom",
      flex: 1,
    },
    {
      field: "telephone",
      headerName: "Téléphone",
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "profession",
      headerName: "Profession",
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (params) => {
        const actions = [];

        if (hasPermission("client", "edit")) {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => selectClient(params.row, "Edit")}
              color="inherit"
            />
          );
        }

        if (hasPermission("client", "delete")) {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => selectClient(params.row, "Delete")}
              color="inherit"
            />
          );
        }

        return actions;
      },
    },
  ];

  useEffect(() => {
    requestGet();
    requestGetCategorie();
  }, []);

  return (
    <Box m="25px">
      <NavigationHeader title="Clients" subtitle="Liste des Clients" />

      <Box display="flex" justifyContent="end" mt="20px">
        {hasPermission("client", "add") && (
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            onClick={() => setModalAdd(true)}
            disabled={isLoading}
          >
            Nouveau Client
          </Button>
        )}
        <AddClientModal
          open={modalAdd}
          handleClose={() => {
            openCloseModalInsert();
            clearSelected();
          }}
          handleSubmit={async (e) => {
            e.preventDefault();
            await requestPost();
          }}
          clientData={clientSelected}
          handleChange={handleChange}
          categories={dataCategorie}
          isLoading={isLoading}
        />

        <EditClientModal
          open={modalEdit}
          handleClose={() => {
            openCloseModalEdit();
            clearSelected();
          }}
          handleSubmit={async (e) => {
            e.preventDefault();
            await requestPut();
          }}
          clientData={clientSelected}
          handleChange={handleChange}
          categories={dataCategorie}
          isLoading={isLoading}
        />

        <DeleteClientModal
          open={modalDelete}
          handleClose={() => {
            setModalDelete(false);
            setDeleteClient(null);
          }}
          handleDelete={requestDelete}
          client={deleteClient} // Use the separate delete state
          isLoading={isLoading}
        />
      </Box>

      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.secondary,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[200]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          getRowId={(row) => row.id_client}
          columns={columns}
          slots={{
            toolbar: CustomToolbar,
          }}
          loading={isLoading}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </Box>
  );
}

const csvOptions = {
  fileName: "ClientsDatabase",
  delimiter: ";",
  utf8WithBom: true,
};

const printOptions = {
  hideFooter: true,
  hideToolbar: true,
  includeCheckboxes: true,
};

function CustomExportButtons(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem options={csvOptions} />
      <GridPrintExportMenuItem options={printOptions} />
    </GridToolbarExportContainer>
  );
}

function CustomToolbar(props) {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <CustomExportButtons />
      <Box
        sx={{
          marginLeft: "auto",
          pl: 0,
          pr: 0,
          pb: 0,
          pt: 0,
        }}
      >
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
}

export default Client;
